import { useEffect, useState } from 'react';
import { BiddingFileResponse, listBiddingFiles } from 'services/actions/ai_management_files';

export interface EditalFile {
  key: string;
  name: string;
  folder: string;
  size: number | null;
  modifiedAt: string | null;
  archive: string | null;
  downloadUrl: string;
}

interface State {
  id: number | null;
  files: EditalFile[];
  error: boolean;
}

const cache = new Map<number, EditalFile[]>();

const folderOf = (path?: string | null) => {
  const parts = (path ?? '').split('/');
  parts.pop();
  return parts.filter(Boolean).join('/');
};

const toEditalFile = (file: BiddingFileResponse): EditalFile => ({
  key: file.id,
  name: file.filename,
  folder: folderOf(file.path),
  size: typeof file.size === 'number' ? file.size : null,
  modifiedAt: file.modified_at ?? null,
  archive: file.archive ?? null,
  downloadUrl: file.download_url,
});

const byFolderThenName = (a: EditalFile, b: EditalFile) =>
  a.folder.localeCompare(b.folder, 'pt-BR') || a.name.localeCompare(b.name, 'pt-BR');

const initialState = (biddingId: number | null): State => {
  const cached = biddingId ? cache.get(biddingId) : undefined;
  return cached
    ? { id: biddingId, files: cached, error: false }
    : { id: null, files: [], error: false };
};

export function useEditalFiles(biddingId: number | null) {
  const [state, setState] = useState<State>(() => initialState(biddingId));

  useEffect(() => {
    if (!biddingId) {
      setState({ id: null, files: [], error: false });
      return undefined;
    }

    const cached = cache.get(biddingId);
    if (cached) {
      setState({ id: biddingId, files: cached, error: false });
      return undefined;
    }

    let active = true;

    listBiddingFiles(biddingId)
      .then((response) => {
        if (!active) return;
        const list = response?.data?.files;
        if (!Array.isArray(list)) {
          setState({ id: biddingId, files: [], error: true });
          return;
        }
        const resolved = list.map(toEditalFile).sort(byFolderThenName);
        cache.set(biddingId, resolved);
        setState({ id: biddingId, files: resolved, error: false });
      })
      .catch((err) => {
        console.error(`Erro ao listar os arquivos do edital da licitacao ${biddingId}:`, err);
        if (active) setState({ id: biddingId, files: [], error: true });
      });

    return () => {
      active = false;
    };
  }, [biddingId]);

  const settled = state.id === biddingId;

  return {
    files: settled ? state.files : [],
    loading: !!biddingId && !settled,
    error: settled && state.error,
  };
}
